import { createSelector, EntityId } from "@reduxjs/toolkit";

import { DateTime } from "luxon";

import { RootState } from "app/store";
import { getSiteDeviceSelectionBySiteId } from "./helpers";
import { userSelectors } from "features/user/store/selectors";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { LiveStreamFeedType } from "../types/LiveStreamFeedType";

const defaultGridViewMaxNumberOfDevicesPerGridRow = 3; // Default value if no devices are selected

const selectActiveSiteId = (state: RootState) =>
    state.liveStreaming.activeSiteId;

const selectSiteDeviceSelections = (state: RootState) =>
    state.liveStreaming.siteDeviceSelections;

const selectActiveSiteDeviceSelection = (state: RootState) =>
    (state.liveStreaming.activeSiteId &&
        getSiteDeviceSelectionBySiteId(
            state.liveStreaming,
            state.liveStreaming.activeSiteId,
        )) ||
    undefined;

const selectFullscreenDeviceId = (state: RootState) =>
    state.liveStreaming.fullscreenDeviceId;

const selectSessionOrigin = (state: RootState) =>
    state.liveStreaming.sessionOrigin;

const selectSession = (state: RootState) => ({
    origin: state.liveStreaming.sessionOrigin,
    siteId: state.liveStreaming.sessionOriginSiteId,
    startTime: state.liveStreaming.sessionStartTime,
});

const selectSelectedDeviceIdsForActiveSite = (state: RootState) =>
    (state.liveStreaming.activeSiteId &&
        getSiteDeviceSelectionBySiteId(
            state.liveStreaming,
            state.liveStreaming.activeSiteId,
        )?.selectedDevices?.map((item) => item.deviceId)) ||
    undefined;

const selectDeviceSelectionForSiteIsDoneByPaging = (
    state: RootState,
    siteId: EntityId,
) =>
    getSiteDeviceSelectionBySiteId(state.liveStreaming, siteId)
        ?.deviceSelectionIsDoneByPaging || false;

const selectDeviceSelectionForSitePageNumber = (
    state: RootState,
    siteId: EntityId,
) =>
    getSiteDeviceSelectionBySiteId(state.liveStreaming, siteId)?.pageNumber ||
    1;

const selectHasBadNetworkConnection = (
    state: RootState,
    minutesBack: number,
) => {
    const activeSiteSelection =
        (state.liveStreaming.activeSiteId &&
            getSiteDeviceSelectionBySiteId(
                state.liveStreaming,
                state.liveStreaming.activeSiteId,
            )) ||
        undefined;
    if (!activeSiteSelection || !activeSiteSelection.devicesWithBadConnection) {
        return false;
    }
    const minutesAgo = DateTime.now().minus({ minutes: minutesBack });

    return activeSiteSelection.devicesWithBadConnection.some(
        (device) =>
            DateTime.fromISO(device.badNetworkDetectedTime) > minutesAgo,
    );
};

const selectNumberOfDevicesPerRow = createSelector(
    [
        selectActiveSiteDeviceSelection,
        userSelectors.selectGridViewEnableAutoSizing,
        userSelectors.selectGridViewMaxNumberOfDevicesPerGridRow,
    ],
    (
        activeSiteDeviceSelection,
        gridViewEnableAutoSizing,
        gridViewMaxNumberOfDevicesPerGridRow,
    ) => {
        if (
            !activeSiteDeviceSelection ||
            !activeSiteDeviceSelection.selectedDevices ||
            !gridViewEnableAutoSizing
        ) {
            return defaultGridViewMaxNumberOfDevicesPerGridRow;
        }
        return Math.min(
            gridViewMaxNumberOfDevicesPerGridRow,
            activeSiteDeviceSelection.selectedDevices.length || 1,
        );
    },
);

const selectAllowedLiveStreamFeedTypes = createSelector(
    [
        (state: RootState) =>
            appFeaturesSelectors.selectFeatureVariant(
                state,
                AppFeature.LiveStreamingAllowedLiveStreamFeedTypes,
            ),
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.LiveStreamingStreamAnonymization,
            ]),
    ],
    (allowedLiveStreamFeedTypes, isStreamAnonymizationEnabled) => {
        if (!isStreamAnonymizationEnabled) {
            return [LiveStreamFeedType.Original];
        }

        return allowedLiveStreamFeedTypes as LiveStreamFeedType[];
    },
);

const selectDefaultLiveStreamFeedType = createSelector(
    [
        selectAllowedLiveStreamFeedTypes,
        selectSessionOrigin,
        userSelectors.selectShowUnrestrictedFeedsByDefault,
    ],
    (
        allowedLiveStreamFeedTypes,
        sessionOrigin,
        showUnrestrictedFeedsByDefault,
    ) => {
        const preferOriginalFeed =
            sessionOrigin === "landing-page" || showUnrestrictedFeedsByDefault;

        if (
            preferOriginalFeed &&
            allowedLiveStreamFeedTypes.indexOf(LiveStreamFeedType.Original) > -1
        ) {
            return LiveStreamFeedType.Original;
        }

        if (
            allowedLiveStreamFeedTypes.indexOf(
                LiveStreamFeedType.FaceAnonymized,
            ) > -1
        ) {
            return LiveStreamFeedType.FaceAnonymized;
        }

        return LiveStreamFeedType.PersonAnonymized;
    },
);

export const liveStreamingSelectors = {
    selectActiveSiteId,
    selectSiteDeviceSelections,
    selectActiveSiteDeviceSelection,
    selectFullscreenDeviceId,
    selectSessionOrigin,
    selectSession,
    selectSelectedDeviceIdsForActiveSite,
    selectDeviceSelectionForSiteIsDoneByPaging,
    selectDeviceSelectionForSitePageNumber,
    selectHasBadNetworkConnection,
    selectNumberOfDevicesPerRow,
    selectAllowedLiveStreamFeedTypes,
    selectDefaultLiveStreamFeedType,
};
